import React from 'react';
import { 
  Activity, 
  Target, 
  Zap, 
  Heart, 
  RefreshCcw,
  ArrowRight 
} from 'lucide-react';
import { Link } from 'react-router-dom';
import { Banner } from './Banner';

export const Services = () => {
  const services = [
    {
      icon: Activity,
      title: "Sports Injury Recovery",
      slug: "blog/sports-injury-recovery",
      description: "Specialized treatment for athletic injuries including sprains, strains, and overuse injuries. Get back to peak performance safely.",
      features: ["Movement Analysis", "Sport-Specific Training", "Injury Prevention"]
    },
    {
      icon: Target,
      title: "Orthopedic Rehabilitation",
      slug: "blog/physical-therapy-after-joint-replacement-what-you-need-to-know",
      description: "Expert care for post-surgical recovery and orthopedic conditions affecting bones, joints, and muscles.",
      features: ["Post-Surgical Rehab", "Joint Mobility", "Pain Management"]
    },
    {
      icon: Zap,
      title: "Manual Therapy",
      slug: "blog/manual-techniques-in-physical-therapy-what-are-they-and-why-they-work",
      description: "Advanced hands-on treatment techniques to improve mobility, reduce pain, and restore normal function using specialized manual interventions.",
      features: ["Myofascial Release", "Integrative Dry Needling", "Cupping", "Kinesiotaping", "Joint Mobilization", "And More"]
    },
    {
      icon: RefreshCcw,
      title: "Vertigo & Dizziness Care",
      slug: "blog/what-is-vertigo-and-how-can-physical-therapy-help-you-stop-the-spinning",
      description: "Evaluation and treatment for BPPV and other vestibular issues causing dizziness, spinning sensations, and imbalance.",
      features: [
        "BPPV (Benign Paroxysmal Positional Vertigo) assessment",
        "Canalith repositioning maneuvers (Epley, Semont)",
        "Gaze stabilization exercises (VOR training)",
        "Habituation exercises for motion sensitivity",
        "Balance retraining and fall risk reduction",
        "Education on triggers and self-management"
      ]
    }
  ];

  return (
    <>
      {/* Shared Banner + Pill (reduced) with title fed into Banner */}
      <Banner pillText="Our Services" title="GymRatRehab Services available in the Phoenix, AZ Metro area (SouthEast Valley)" subtitle="Specialized treatment for sports injuries and orthopedic conditions. Evidence-based approach gets you back to doing what you love." reduced={true} titleClassName="text-[85%] lg:text-[85%]" />

      <section id="services" className="pt-8 pb-20">
        <div className="container">
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-8">
          {services.map((service, index) => {
            const IconComponent = service.icon;
            return (
              <div key={index} className="service-card group">
                <div className="flex items-center justify-center w-16 h-16 rounded-xl mb-6 transition-colors" style={{ backgroundColor: 'rgba(232, 184, 109, 0.15)' }}>
                  <IconComponent style={{ color: 'var(--brand-primary)' }} size={28} />
                </div>
                
                <h3 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>
                  {service.title}
                </h3>
                
                <p className="body-medium mb-6">
                  {service.description}
                </p>
                
                <ul className="space-y-2 mb-6">
                  {service.features.map((feature, featureIndex) => (
                    <li key={featureIndex} className="flex items-center text-gray-400 text-sm">
                      <div className="w-1.5 h-1.5 rounded-full mr-3" style={{ backgroundColor: 'var(--brand-primary)' }}></div>
                      {feature}
                    </li>
                  ))}
                </ul>
                
                <Link to={`/${service.slug || ''}`} className="flex items-center font-semibold transition-colors group" style={{ color: 'var(--brand-primary)' }}>
                  Learn More
                  <ArrowRight size={16} className="ml-2 group-hover:translate-x-1 transition-transform" />
                </Link>
              </div>
            );
          })}
        </div>

        <div className="text-center mt-16">
          <button 
            className="btn-primary text-lg px-8 py-4"
            onClick={() => {
              window.location.href = '/contact?focus=contact';
            }}
          >
            Book Vertigo Evaluation
          </button>
        </div>

        {/* Pricing Summary Section */}
        <div className="mt-20 pt-16 border-t border-gray-800">
          <div className="text-center mb-12">
            <h3 className="heading-2 mb-6" style={{ color: 'var(--brand-primary)' }}>
              Cash-Based Practice Pricing
            </h3>
            <p className="body-large max-w-3xl mx-auto mb-8">
              We're a cash-based practice focused on providing personalized, one-on-one treatment 
              without insurance restrictions. This allows us to dedicate the time needed for your optimal recovery.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            <div className="pricing-card text-center p-8 rounded-xl border transition-colors hover:border-opacity-80" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h4 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>
                Initial Consultation &amp; Evaluation
              </h4>
              <div className="text-4xl font-bold mb-4" style={{ color: 'var(--brand-primary)' }}>
                $150
              </div>
              <p className="body-medium text-gray-400">
                Comprehensive assessment and personalized treatment plan development
              </p>
            </div>

            <div className="pricing-card text-center p-8 rounded-xl border transition-colors hover:border-opacity-80" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h4 className="heading-3 mb-4" style={{ color: 'var(--brand-primary)' }}>
                Follow-Up Treatment Sessions
              </h4>
              <div className="text-4xl font-bold mb-4" style={{ color: 'var(--brand-primary)' }}>
                $125
              </div>
              <p className="body-medium text-gray-400">
                One-hour individualized treatment sessions
              </p>
            </div>
          </div>

          <div className="mt-12 max-w-3xl mx-auto">
            <div className="bg-gray-900 rounded-xl p-8 border" style={{ borderColor: 'var(--border-brick-subtle)' }}>
              <h4 className="heading-4 mb-6 text-center" style={{ color: 'var(--brand-primary)' }}>
                Special Discounts Available
              </h4>
              <div className="grid md:grid-cols-2 gap-6">
                <div className="text-center">
                  <p className="font-semibold mb-2">Alexander Medical Center Patients</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>$25 off each visit</p>
                </div>
                <div className="text-center">
                  <p className="font-semibold mb-2">Chandler Health &amp; Performance Members</p>
                  <p className="text-lg font-bold" style={{ color: 'var(--brand-primary)' }}>$25 off each visit</p>
                </div>
              </div>
            </div>
          </div>

          <div className="text-center mt-8">
            <a href="/pricing" className="inline-flex items-center font-semibold transition-colors" style={{ color: 'var(--brand-primary)' }}>
              View Complete Pricing &amp; Treatment Packages
              <ArrowRight size={16} className="ml-2" />
            </a>
          </div>
        </div>
        </div>
      </section>
    </>
  );
};